/**
 * One AI employee, nested under the person it reports to: what it does, its
 * routine (schedules), what it's working on, questions waiting for a human
 * (answer right here), what it finished, its CRM tasks, and a box to give it
 * new work. It lives on the SyncSuite platform; this card is a window on it.
 */
import { useState } from "react";
import { AlertTriangle, Bot, CheckCircle2, ChevronDown, Clock, Loader2, MessageCircleQuestion, Send, XCircle } from "lucide-react";
import { api, ApiError } from "@/lib/api";
import { Button, Input, Select, Textarea, useToast } from "@/components/ui";
import { cn } from "@/lib/utils";
import { firstName, timeAgo } from "@/features/command/ui";
import { TaskRow } from "../TaskRow";
import { scheduleLabel } from "../lib";
import type { Agent, AgentTask, Person, TeamData, WorkTask } from "../types";

export function AgentCard({
  agent, people, team, wsTz, onChanged, onOpenTask, onToggleTask,
}: {
  agent: Agent;
  people: Person[];
  team: TeamData;
  wsTz: string;
  onChanged: () => void;
  onOpenTask: (t: WorkTask) => void;
  onToggleTask: (t: WorkTask) => void;
}) {
  const toast = useToast();
  const [giving, setGiving] = useState(false);
  const [title, setTitle] = useState("");
  const [brief, setBrief] = useState("");
  const [busy, setBusy] = useState(false);
  const [showRecent, setShowRecent] = useState(false);
  const working = agent.active.filter((t) => t.status !== "waiting" || t.waitingOn !== "human");

  const setManager = async (managerId: string) => {
    try {
      await api.put(`/team/agents/${encodeURIComponent(agent.slug)}`, { manager_id: managerId || null, role: agent.role });
      const boss = people.find((p) => p.id === managerId);
      toast.success(boss ? `${agent.name} now reports to ${firstName(boss.name)}` : `${agent.name} reports to nobody for now`);
      onChanged();
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : "Couldn't change who it reports to");
    }
  };

  const give = async () => {
    if (!title.trim()) return;
    setBusy(true);
    try {
      await api.post("/team/assign", { agent_slug: agent.slug, title: title.trim(), brief: brief.trim() || undefined });
      toast.success(`Handed to ${agent.name}`, "It shows under \"Working on\" once it starts.");
      setTitle(""); setBrief(""); setGiving(false);
      onChanged();
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : "Couldn't hand that over");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-w-0 rounded-2xl border border-indigo-400/15 bg-indigo-500/[0.04] p-3.5">
      {/* Who */}
      <div className="flex items-start gap-3">
        <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl cc-brand-bg shadow-lg shadow-indigo-500/20">
          <Bot className="h-4 w-4 text-white" />
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5">
            <span className="text-sm font-semibold text-zinc-50">{agent.name}</span>
            {agent.role && <span className="text-xs text-indigo-200/80">{agent.role}</span>}
          </div>
          {agent.description && <p className="mt-0.5 line-clamp-2 text-xs leading-relaxed text-zinc-400">{agent.description}</p>}
        </div>
        <label className="hidden shrink-0 items-center gap-1.5 text-[11px] text-zinc-500 sm:flex">
          Reports to
          <Select value={agent.manager_id ?? ""} onChange={(e) => void setManager(e.target.value)} className="h-7 w-28 text-xs">
            <option value="">Nobody yet</option>
            {people.map((p) => <option key={p.id} value={p.id}>{firstName(p.name)}</option>)}
          </Select>
        </label>
      </div>
      <label className="mt-2 flex items-center gap-1.5 text-[11px] text-zinc-500 sm:hidden">
        Reports to
        <Select value={agent.manager_id ?? ""} onChange={(e) => void setManager(e.target.value)} className="h-7 flex-1 text-xs">
          <option value="">Nobody yet</option>
          {people.map((p) => <option key={p.id} value={p.id}>{firstName(p.name)}</option>)}
        </Select>
      </label>

      {/* Its routine */}
      {agent.schedules.length > 0 && (
        <div className="mt-3 space-y-1">
          {agent.schedules.map((s, i) => (
            <div key={`${s.name}:${i}`} className="flex items-start gap-1.5 text-[11px] text-zinc-400">
              <Clock className="mt-px h-3 w-3 shrink-0 text-zinc-500" />
              <span className="min-w-0">
                <span className="text-zinc-300">{s.name}</span>: {scheduleLabel(s)}
                {s.lastError && <span className="ml-1 text-rose-300">(last run failed)</span>}
              </span>
            </div>
          ))}
        </div>
      )}

      {/* Needs your answer */}
      {agent.waiting_on_you.length > 0 && (
        <div className="mt-3 space-y-2">
          {agent.waiting_on_you.map((q) => <Question key={q.id} q={q} agentName={agent.name} onAnswered={onChanged} />)}
        </div>
      )}

      {/* Working on */}
      <div className="mt-3">
        <div className="mb-1 text-[11px] font-semibold uppercase tracking-wider text-zinc-500">Working on</div>
        {working.length === 0 ? (
          <div className="text-xs text-zinc-500">Nothing right now.</div>
        ) : (
          <ul className="space-y-1">
            {working.map((t) => (
              <li key={t.id} className="flex items-center gap-2 text-xs">
                {t.status === "working" ? <Loader2 className="h-3 w-3 shrink-0 animate-spin text-indigo-300" /> : <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-zinc-500" />}
                <span className="min-w-0 flex-1 truncate text-zinc-200">{t.title}</span>
                <span className="shrink-0 text-[10px] text-zinc-500">
                  {t.status === "waiting" && t.waitingOn && t.waitingOn !== "human" ? `waiting on ${t.waitingOn.name}` : t.status === "working" ? "working" : "queued"}
                </span>
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* CRM tasks it does */}
      {agent.crm_tasks.length > 0 && (
        <div className="mt-3">
          <div className="mb-1 text-[11px] font-semibold uppercase tracking-wider text-zinc-500">Its tasks here</div>
          <div className="-mx-3.5 divide-y divide-zinc-800/40">
            {agent.crm_tasks.map((t) => (
              <TaskRow key={t.id} t={t} team={team} people={people} wsTz={wsTz} onToggle={() => onToggleTask(t)} onOpen={() => onOpenTask(t)} className="py-2" />
            ))}
          </div>
        </div>
      )}

      {/* Recent results */}
      {agent.recent.length > 0 && (
        <div className="mt-3">
          <button type="button" onClick={() => setShowRecent((v) => !v)} className="inline-flex items-center gap-1 text-[11px] font-semibold uppercase tracking-wider text-zinc-500 hover:text-zinc-200">
            What it finished ({agent.recent.length})
            <ChevronDown className={cn("h-3 w-3 transition-transform", showRecent && "rotate-180")} />
          </button>
          {showRecent && <div className="mt-1.5 space-y-1">{agent.recent.map((r) => <Result key={r.id} r={r} />)}</div>}
        </div>
      )}

      {/* Give it a task */}
      <div className="mt-3 border-t border-indigo-400/10 pt-3">
        {!giving ? (
          <button type="button" onClick={() => setGiving(true)} className="inline-flex items-center gap-1.5 text-xs font-medium text-indigo-300 hover:text-indigo-200">
            <Send className="h-3.5 w-3.5" /> Give it a task
          </button>
        ) : (
          <form onSubmit={(e) => { e.preventDefault(); void give(); }} className="space-y-2">
            <Input autoFocus value={title} onChange={(e) => setTitle(e.target.value)} placeholder="What should it do? e.g. Find 5 podcasts to pitch" />
            <Textarea value={brief} onChange={(e) => setBrief(e.target.value)} placeholder="Details, links, what good looks like (optional)" className="min-h-[3.5rem]" />
            <div className="flex justify-end gap-2">
              <Button type="button" size="sm" variant="ghost" onClick={() => setGiving(false)}>Cancel</Button>
              <Button type="submit" size="sm" loading={busy} disabled={!title.trim()} leftIcon={<Send className="h-3.5 w-3.5" />}>Hand it over</Button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}

function Question({ q, agentName, onAnswered }: { q: AgentTask; agentName: string; onAnswered: () => void }) {
  const toast = useToast();
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const send = async () => {
    if (!text.trim()) return;
    setBusy(true);
    try {
      await api.post("/team/reply", { agent_task_id: q.id, message: text.trim() });
      toast.success(`Sent to ${agentName}`, "It picks the work back up from here.");
      setText("");
      onAnswered();
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : "Couldn't send that");
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="rounded-xl border border-amber-400/25 bg-amber-500/10 p-3">
      <div className="flex items-start gap-2">
        <MessageCircleQuestion className="mt-0.5 h-4 w-4 shrink-0 text-amber-300" />
        <div className="min-w-0 flex-1">
          <div className="text-[11px] font-semibold uppercase tracking-wider text-amber-200/90">Needs your answer</div>
          <p className="mt-0.5 whitespace-pre-wrap text-sm text-zinc-100">{q.question || "It's waiting for a person to reply."}</p>
          <div className="mt-0.5 text-[11px] text-zinc-500">On "{q.title}", asked {timeAgo(q.updatedAt)}</div>
        </div>
      </div>
      <form onSubmit={(e) => { e.preventDefault(); void send(); }} className="mt-2 flex items-end gap-2">
        <Textarea
          value={text}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) void send(); }}
          placeholder="Your answer"
          className="min-h-[2.5rem] flex-1"
          rows={2}
        />
        <Button type="submit" size="sm" loading={busy} disabled={!text.trim()} leftIcon={<Send className="h-3.5 w-3.5" />}>Send</Button>
      </form>
    </div>
  );
}

function Result({ r }: { r: AgentTask }) {
  const [open, setOpen] = useState(false);
  const ok = r.status === "done";
  return (
    <div className="rounded-lg border border-zinc-800/70 bg-zinc-900/40">
      <button type="button" onClick={() => setOpen((v) => !v)} className="flex w-full items-center gap-2 px-2.5 py-1.5 text-left">
        {ok ? <CheckCircle2 className="h-3.5 w-3.5 shrink-0 text-emerald-400" /> : r.status === "failed" ? <AlertTriangle className="h-3.5 w-3.5 shrink-0 text-rose-400" /> : <XCircle className="h-3.5 w-3.5 shrink-0 text-zinc-500" />}
        <span className="min-w-0 flex-1 truncate text-xs text-zinc-200">{r.title}</span>
        <span className="shrink-0 text-[10px] text-zinc-500">{r.status === "failed" ? "didn't work, " : r.status === "cancelled" ? "stopped, " : ""}{timeAgo(r.completedAt ?? r.updatedAt)}</span>
        <ChevronDown className={cn("h-3 w-3 shrink-0 text-zinc-500 transition-transform", open && "rotate-180")} />
      </button>
      {open && (
        <div className="max-h-72 overflow-y-auto whitespace-pre-wrap border-t border-zinc-800/70 px-2.5 py-2 text-xs leading-relaxed text-zinc-300">
          {r.result || "No write-up was left for this one."}
        </div>
      )}
    </div>
  );
}
